/**
 * UsageCard — the Settings page's at-a-glance "where do I stand on coach
 * responses" panel. Replaces the old one-line `UsageLine`, which only
 * described the single binding window and left users guessing what the
 * tiny global UsageChip meant.
 *
 * Renders one of three states:
 *
 *  1. Unlimited subscriber → a clean "no limits" affirmation.
 *  2. Free, pre-first-leak-report → progress toward the 25-signal unlock
 *     (signal counts ARE shown numerically — they're not a cost meter).
 *  3. Capped (free post-25, or core/plus) → one row per active cap. Free
 *     shows BOTH daily + monthly so users see the full picture. Each row
 *     names the window, states the plan's allowance, shows a
 *     usage-proportional status bar + qualitative tag, and a live reset
 *     countdown.
 *
 * The bar fills proportionally to `used / limit` (to scale, matching the
 * AppShell radial dial) but deliberately does NOT print the live `used`
 * count (e.g. "6 / 10") — the bar + qualitative tag convey state without a
 * leakable per-response number, per the no-raw-numbers rule (see billing
 * CLAUDE.md). Signal progress is exempt and shown exactly.
 */

import {
  capLevel,
  type BillingStatus,
  type CapLevel,
  type CapWindow,
} from "@/features/billing/lib/billing";
import { useResetCountdown } from "@/shared/resetCountdown";

export function UsageCard({ status }: { status: BillingStatus }) {
  const isPaid =
    status.subscriptionStatus === "active" ||
    status.subscriptionStatus === "past_due";

  // 1. Unlimited subscribers — no meter applies.
  if (isPaid && status.planLevel === "unlimited") {
    return (
      <CardShell>
        <div className="flex items-baseline justify-between gap-3">
          <div className="text-sm font-medium text-text">Coach responses</div>
          <div className="text-xs font-semibold uppercase tracking-wider text-accent">
            Unlimited
          </div>
        </div>
        <p className="text-xs text-text-muted leading-relaxed">
          No daily or monthly limit — drill as much as you want. Library
          spots, leak reports, and quizzes are free too.
        </p>
      </CardShell>
    );
  }

  // 2. Free, pre-first-leak-report — positive progress toward unlock.
  const preFirstReport =
    status.subscriptionStatus === "free" &&
    status.signalCount < status.signalsForFirstReport;
  if (preFirstReport) {
    const pct = status.signalsForFirstReport
      ? Math.min(100, (status.signalCount / status.signalsForFirstReport) * 100)
      : 0;
    return (
      <CardShell>
        <div className="flex items-baseline justify-between gap-3">
          <div className="text-sm font-medium text-text">
            Coach responses
          </div>
          <div className="text-xs font-semibold uppercase tracking-wider text-text-muted/80">
            Free
          </div>
        </div>
        <p className="text-xs text-text-muted leading-relaxed">
          Uncapped until you earn your first leak report. Keep drilling.
        </p>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-text-muted">Toward your first leak report</span>
            <span className="font-mono tabular-nums text-text">
              {status.signalCount} / {status.signalsForFirstReport}
            </span>
          </div>
          <Bar fillPct={pct} tone="accent" />
        </div>
      </CardShell>
    );
  }

  // 3. Capped — one row per active cap (free shows daily + monthly).
  return (
    <CardShell>
      <div className="text-sm font-medium text-text">Coach responses</div>
      <div className="space-y-4">
        {status.caps.map((cap) => (
          <CapRow key={cap.window} cap={cap} />
        ))}
      </div>
      <p className="text-[11px] text-text-muted/80 leading-relaxed">
        A coach response is each time the coach grades a decision or answers a
        question. Library spots, leak reports, and quizzes never count.
      </p>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------

function CapRow({ cap }: { cap: CapWindow }) {
  const level = capLevel(cap);
  const countdown = useResetCountdown(cap.window);

  const windowLabel = cap.window === "daily" ? "Daily" : "Monthly";
  const period = cap.window === "daily" ? "day" : "month";
  const allowance = `${cap.limit.toLocaleString()} coach responses per ${period}`;
  // Proportional fill — to scale with actual usage, matching the AppShell
  // radial dial (which already fills by used/limit). Still no raw numbers
  // printed; the bar + qualitative tag convey state without a leakable count.
  const fillPct = cap.limit > 0 ? (cap.used / cap.limit) * 100 : 0;

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <span className="text-sm font-medium text-text">{windowLabel}</span>
          <span className="text-xs text-text-muted"> · {allowance}</span>
        </div>
        <StatusTag level={level} />
      </div>
      <Bar fillPct={fillPct} tone={toneForLevel(level)} />
      <div className="text-[11px] text-text-muted/70">
        {level === "over" ? "Unlocks" : "Resets"} {countdown}
      </div>
    </div>
  );
}

function StatusTag({ level }: { level: CapLevel }) {
  const { label, cls } =
    level === "over"
      ? { label: "Limit reached", cls: "text-danger" }
      : level === "near"
        ? { label: "Getting close", cls: "text-amber-300" }
        : { label: "Plenty left", cls: "text-text-muted" };
  return <span className={`text-xs font-medium ${cls}`}>{label}</span>;
}

type BarTone = "accent" | "muted" | "amber" | "danger";

function Bar({ fillPct, tone }: { fillPct: number; tone: BarTone }) {
  const fillCls =
    tone === "accent"
      ? "bg-accent"
      : tone === "amber"
        ? "bg-amber-400"
        : tone === "danger"
          ? "bg-danger"
          : "bg-text-muted/60";
  return (
    <div className="h-1.5 w-full rounded-full bg-border/60 overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${fillCls}`}
        style={{ width: `${Math.max(0, Math.min(100, fillPct))}%` }}
      />
    </div>
  );
}

function toneForLevel(level: CapLevel): BarTone {
  if (level === "over") return "danger";
  if (level === "near") return "amber";
  return "muted";
}

function CardShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-bg/40 p-4 space-y-3">
      {children}
    </div>
  );
}
